/**
 * Canonical enumerations for the model. This file is pure (no DB, no framework)
 * and is the single source of truth: `src/db/schema.ts` builds its Postgres
 * enums from these arrays, and the domain logic derives its union types from
 * them. Changing a value here changes both sides at once.
 */

/**
 * Contract spine (docs/CONTRACT_SPINE.md, settled 2026-07-06; re-cut by
 * docs/CLOSED_AXIS.md + docs/FLOW_DEFINITION.md §11). The old single `status`
 * enum became orthogonal axes; the lifecycle is exactly the three atom
 * columns (`delivered`/`active`/`closed`, plain booleans - no enum), and NO
 * platform phase vocabulary exists anywhere: richer words are a flow's
 * authored `means` mappings, and the coarse lane word `open · active ·
 * closed` was retired with the standing word - the atoms speak for
 * themselves (domain/lane-attributes.ts).
 *
 * **verdict** is the requester's statement against the definition (§5.2). Scoped
 * to the delivery it judged: leaving `done` clears a standing verdict back to
 * `open` in the same event (a definitional part of the act, not an inferred side
 * effect, §6). Nothing is terminal; reopening is first-class.
 */
export const VERDICTS = ['open', 'disputed', 'accepted'] as const;
export type Verdict = (typeof VERDICTS)[number];

/**
 * Seat state (§2). A held seat is a filled party (the `requesters`/`contributors`
 * arrays); an offered seat is a pending push (the `seat_offers` table) awaiting
 * take-up; open = empty ("needs doing", first-class). Self-claim goes straight to
 * `held`; seating someone else lands `held` or `offered` by their push rules.
 */
export const SEAT_STATES = ['open', 'offered', 'held'] as const;
export type SeatState = (typeof SEAT_STATES)[number];

/** The two seats of a contract (§0): the requester (owns the what, holds the
 *  verdict; seated at birth, and empties only via an explicit logged walk-away -
 *  vacancy semantics in docs/HEALTH_SIGNALS.md §4) and the contributor (does
 *  the work, empty = open). */
export const SEAT_ROLES = ['requester', 'contributor'] as const;
export type SeatRole = (typeof SEAT_ROLES)[number];

/**
 * Push-rule mode (§2, D3): who may bind a subject to a held seat directly.
 * `circle` = an explicit circle of refs seat directly to `held`, anyone else
 * lands an offer; `ask_all` = "protected", an empty circle read backwards
 * (everyone must ask). **No push_rules row = the default circle** (co-members of
 * the subject's own groups), resolved by the server and passed to the pure
 * `resolvePushLanding`.
 */
export const PUSH_MODES = ['circle', 'ask_all'] as const;
export type PushMode = (typeof PUSH_MODES)[number];

/** MoSCoW priority, owned by the requester. */
export const PRIORITIES = ['must', 'should', 'could'] as const;
export type Priority = (typeof PRIORITIES)[number];

/**
 * Item visibility, the **audience dial** (MODEL.md §6, reframed 2026-06-26). Sight is
 * **private by default**: `protected` is now the default and means *union-only* sight
 * (visible to the groups it is granted to, its owners, and explicit grants, never the
 * whole org). `open` is the **opt-in** "opened up" choice: sight for everyone with
 * standing, every access logged - the org in a jurisdiction, everyone on Tesserano
 * in the commons (docs/COMMONS.md). Viewing is earned by opening up. (Values kept
 * as `open`/`protected` to avoid an enum-rename migration; `protected` reads as
 * "private". The board item "Known gaps (July): access and tenancy" notes the
 * optional rename.)
 */
export const VISIBILITIES = ['open', 'protected'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

/**
 * Access roles (MODEL.md §6), a named bundle of capabilities, ascending so the
 * union's "highest role wins" is an index comparison:
 *   - **viewer**: can see it, nothing more (doubles as the guest / external rung).
 *   - **member**: works in it, can act on the work shared with it.
 *   - **owner**: runs the *group* and shapes its membership (never owns the work;
 *     items float). On a work-item grant, the strongest reach.
 * The deep, item-specific powers (accept, dispute, set criteria) come from the
 * **contract** (requester/contributor), not from the role; the role governs reach.
 */
export const ROLES = ['viewer', 'member', 'owner'] as const;
export type Role = (typeof ROLES)[number];

/**
 * The kinds of stored view (docs/MODEL.md: a view is a shared lens, never a
 * container). `board` is the shared place that holds work, drawn in one of its
 * layouts (kanban, list, checklist, docs/VIEWS.md §3, `BoardView.layout`).
 * `map` (the saved map view, converted to lenses by migration 0040) and `list`
 * (the columnar table, converted to boards on the list or checklist layout by
 * migration 0116) are RETIRED values the enum keeps (append-only): no row
 * carries them and no writer inserts them. All ride the same `views` row (scope + name + config payload): a view carries
 * **only its own config** (filters, hide set, ordering, columns), never any
 * work-item data, so the items stay the single source of truth and the board is
 * just a stable, reliable way to organize and interact with them, personal or
 * group-shared.
 *
 * `lens` is the way-of-looking kind (docs/LENSES.md §2): a view that IS its
 * config and nothing else - criteria + per-rail arrangement + a default rail,
 * no membership, no item reach, no flow link, conferring no access by kind.
 * It always evaluates through the opener's own grants ("these criteria,
 * through YOUR eyes"); payload shape in `domain/lens-view.ts`.
 *
 * NOTE on `'stagegate'`: the pre-flatten pg enum (old migration 0025) carried
 * it as a reserved-but-unbound value; the flattened baseline never created it,
 * and migration 0037's `view_kind` recreate settles every live DB on exactly
 * this list. If saved flow-portfolio views are built (the board item "Context
 * resolution leftovers: flow drift flag, item-anchored flows, saved
 * flow-portfolio views"), the kind lands here + in `ViewPayload` + a generated
 * enum migration together. Any read that encounters an unknown kind must fail
 * loud, not silently coerce (`normalizeViewPayload`).
 */
export const VIEW_KINDS = ['map', 'board', 'list', 'lens'] as const;
export type ViewKind = (typeof VIEW_KINDS)[number];

/**
 * A view row's lifecycle (docs/LENSES.md §5): `active` is the living default;
 * `retired` is the read-only tombstone a LENS leaves its subscribers (config
 * stays copyable via fork; nothing else was ever there). Retirement is a
 * lens-kind act - tables delete (`view_deleted`), lenses retire - but the
 * column lives on the one `views` row all kinds share.
 */
export const VIEW_STATUSES = ['active', 'retired'] as const;
export type ViewStatus = (typeof VIEW_STATUSES)[number];

/** Rank of a role in the ascending ladder (viewer=0 … owner=2). */
export function roleRank(role: Role): number {
  return ROLES.indexOf(role);
}

/** The stronger of two roles ("highest wins" over a union of access paths). A
 *  null role means "no access by this path"; null loses to any real role. */
export function maxRole(a: Role | null, b: Role | null): Role | null {
  if (a === null) return b;
  if (b === null) return a;
  return roleRank(a) >= roleRank(b) ? a : b;
}

/** Whether `role` meets or exceeds `min` (null = no access, never meets it). */
export function roleAtLeast(role: Role | null, min: Role): boolean {
  return role !== null && roleRank(role) >= roleRank(min);
}

/**
 * State of an access edge (a `work_item_grants` row or a `memberships` row).
 * `active` is in force; `proposed` is a pending half of a two-sided move, an
 * **access request** (a person asking for a role on an item) or a **nesting
 * proposal** (one group's owner asking to nest under another), awaiting the
 * other side's owner to flip it to active. A proposed edge grants nothing until
 * accepted.
 */
export const EDGE_STATUSES = ['active', 'proposed'] as const;
export type EdgeStatus = (typeof EDGE_STATUSES)[number];

/**
 * A contact's state (docs/REACHABILITY.md §2): `pending` until the addressee
 * answers, `active` once they accept, `withdrawn` once the sender withdrew a
 * pending request: it stays for the addressee for 30 days as a quiet row whose
 * only act is Block, so a sender cannot ask, withdraw and repeat without ever
 * leaving a row to block from (pruned lazily after that). Declining and
 * removing DELETE the row rather than parking a state, so a refusal leaves no
 * standing record and no delivery receipt. Its own enum rather than
 * `EDGE_STATUSES`: a contact is identity-plane, off the log, and shares no
 * lifecycle with the graph's edges.
 */
export const CONTACT_STATUSES = ['pending', 'active', 'withdrawn'] as const;
export type ContactStatus = (typeof CONTACT_STATUSES)[number];

/**
 * The throttled lanes of the reachability model (docs/REACHABILITY.md, "the
 * law"): `contact` counts contact requests, `handle` counts attempts to set a
 * handle, `group_ask` counts adds to a group that land as an ask (the
 * stranger landing, §2.4). The first two can tell whether an identifier is
 * held by someone; the third is an unsolicited message to a person. Each
 * counts per actor in a rolling hour (`reach_attempts`, off the log like the
 * rest of the lane).
 */
export const REACH_LANES = ['contact', 'handle', 'group_ask'] as const;
export type ReachLane = (typeof REACH_LANES)[number];

/** How a group role resolves a discrete decision (Committed / Accepted). */
export const DECISION_RULES = ['lead', 'quorum', 'any'] as const;
export type DecisionRule = (typeof DECISION_RULES)[number];

/**
 * The outcome of a **decision** (docs/DECISIONS.md "Primitive 2: Decision"):
 * the recorded, attributed act that judges or moves an item. `advance` / `hold`
 * are the boundary decision (a flow member's crossing: go, or a recorded
 * no-go); `accept` / `dispute` / `resolve` are the verdict decisions. The engine
 * records every one on the `decisions` projection; the values are reserved
 * together so migrating the verdict acts onto the record needs no enum change.
 */
export const DECISION_OUTCOMES = ['advance', 'hold', 'accept', 'dispute', 'resolve'] as const;
export type DecisionOutcome = (typeof DECISION_OUTCOMES)[number];

/**
 * A **ceremony** (docs/DECISIONS.md "Primitive 3: Ceremony") bundles decisions +
 * deciders, modeled on proposals. `open` = convened, its agenda still worked;
 * `closed` = concluded. An agenda item's status mirrors a proposal change:
 * `pending` (prepared, not yet taken), `applied` (executed - the real act ran
 * and recorded its decision), `declined` (the bundle chose not to take it).
 */
export const CEREMONY_STATUSES = ['open', 'closed'] as const;
export type CeremonyStatus = (typeof CEREMONY_STATUSES)[number];

export const CEREMONY_ITEM_STATUSES = ['pending', 'applied', 'declined'] as const;
export type CeremonyItemStatus = (typeof CEREMONY_ITEM_STATUSES)[number];

/**
 * A participant's role in a ceremony (docs/DECISIONS.md "Primitive 3"): who is
 * doing what in the room. `decider` takes decisions (suggested from the agenda's
 * decisions' declared deciders); `facilitator` runs it; `contributor` presents /
 * gives input; `observer` watches. A role is held by a person OR a group ref.
 * Roles are recorded context and suggestion, not a new authority path: executing
 * a decision still routes through the real act's own gate (the paper's law).
 */
export const MEETING_ROLES = ['facilitator', 'decider', 'contributor', 'observer'] as const;
export type MeetingRole = (typeof MEETING_ROLES)[number];

/**
 * Lifecycle of a **proposal** (docs/PROPOSALS.md): a staged, authored changeset over
 * the work graph. `draft` is malleable (mis-en-place); `published` **freezes** it
 * (immutable, the diff is raised to affected owners; further changes are addenda, a new
 * proposal); `withdrawn` retires a draft. A published proposal never goes back to draft.
 */
export const PROPOSAL_STATUSES = ['draft', 'published', 'withdrawn'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

/**
 * Lifecycle of a single staged change inside a proposal. `pending` is the raised diff
 * (unresolved, standing visibly); `applied` went through the item write spine and set the
 * live field (provenance recorded); `declined` was refused (the diff closes, logged);
 * `superseded` was overtaken (the live field moved elsewhere, or an addendum replaced it).
 * Adoption is incremental and per-change; there is no all-or-nothing apply.
 */
export const PROPOSAL_CHANGE_STATUSES = ['pending', 'applied', 'declined', 'superseded'] as const;
export type ProposalChangeStatus = (typeof PROPOSAL_CHANGE_STATUSES)[number];

/**
 * What a staged change carries (docs/PROPOSALS.md, docs/RESOLUTION.md §1 step 5):
 * a `field` change sets one field to a value; an `act` carries a gesture the
 * proposer may not take themselves (the propose lane) - the stimulus, the
 * sentence a dry run drafted for the answerer, and an expiry. Accepting an act
 * runs the stimulus through the resolver as the accepter, attributed to the
 * proposer.
 */
export const PROPOSAL_CHANGE_KINDS = ['field', 'act'] as const;
export type ProposalChangeKind = (typeof PROPOSAL_CHANGE_KINDS)[number];

/**
 * Lifecycle of a **plan** (docs/PROPOSALS.md): an owned collection of proposals (a release
 * branch). `draft` collects proposals (each authored by whoever made it); `published` is the
 * push - the plan owner ships it and every member draft proposal freezes at once, each change
 * still attributed to its author, never laundered into the pusher. (Unrelated to the billing
 * `PLANS` tier enum / `orgs.plan`; this is the roadmap-collection primitive.)
 */
export const PLAN_STATUSES = ['draft', 'published'] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

/**
 * Lifecycle of a **work-item invite** (docs/archive/COMMONS_BUILD_PLAN.md S4): a pending,
 * email-bound grant awaiting a verified account. `pending` awaits its claim;
 * `claimed` converted into a real `work_item_grants` row; `revoked` was withdrawn
 * by the inviter/owner; `expired` passed its expiry. Expiry is **lazy**: a
 * pending row past `expiresAt` is *treated* as expired by the pure
 * `effectiveInviteStatus` everywhere, and the stored status flips (with its
 * event) only when a write path touches it (a claim attempt) - reads never
 * mutate (read-layer law).
 */
export const INVITE_STATUSES = ['pending', 'claimed', 'revoked', 'expired'] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

/** Dependency edge strength. `blocks` is hard, `relates` is soft. */
export const DEPENDENCY_KINDS = ['blocks', 'relates'] as const;
export type DependencyKind = (typeof DEPENDENCY_KINDS)[number];

/**
 * Relationship kinds: every typed edge between work items, unified under one registry
 * (`domain/relationships.ts`, which declares each kind's facets). Two classes:
 *   - **hierarchical** (containment: `parent`) - an **acyclic DAG** (multi-parent) that
 *     rolls up and cascades access; the tree's islands problem is solved by allowing
 *     many parents with per-edge **shares** for additive rollup (docs/RELATIONSHIPS.md).
 *   - **lateral** (`blocks`, `relates`) - a may-be-cyclic network that feeds
 *     reconciliation (blocking) and never rolls up.
 * `DEPENDENCY_KINDS` is the lateral subset (the legacy name, kept until storage folds
 * the `dependencies` table into `relationships`).
 */
export const RELATIONSHIP_KINDS = ['parent', 'blocks', 'relates'] as const;
export type RelationshipKind = (typeof RELATIONSHIP_KINDS)[number];

/** Org billing tier (SaaS only; null for self-host / single tenant). */
export const PLANS = ['free', 'team', 'enterprise'] as const;
export type Plan = (typeof PLANS)[number];

/**
 * Org kind (docs/COMMONS.md, settled 2026-07-10). The **commons** is the one shared
 * default store: any authenticated person has standing there (may create work items,
 * groups, proposals, tags, ceremonies, elevations, access requests). A **jurisdiction**
 * is an opt-in walled org where standing keeps the original semantics: transitive
 * membership of one of the org's groups. Item-level visibility (`visibilityFor`) is
 * identical in both; only what "standing in the org" means differs.
 */
export const ORG_KINDS = ['commons', 'jurisdiction'] as const;
export type OrgKind = (typeof ORG_KINDS)[number];

/** What a graph entity is. The event log records mutations to any of these.
 *  `group` was `group` until 2026-07-21 (the vocabulary rename; migration 0045
 *  rewrote the enum and every stored row - "group" no longer exists anywhere). */
export const SUBJECT_TYPES = [
  'org',
  'group',
  'work_item',
  'dependency',
  'relationship',
  'grant',
  'view',
  'proposal',
  'plan',
  // A first-class ontology definition (a field or a flow) authored on a context
  // anchor. Definition writes ride the `group` subject (the owning context), so
  // they surface in the group's activity, never the item's.
  'definition',
  // A push rule (docs/CONTRACT_SPINE.md §2, D3): who may seat a person/group
  // directly. Authority configuration, so its changes live on the log; the event
  // subject is the `push_rules` row (the person/group it governs rides the
  // payload's subjectRef - person ids are not uuids, so they cannot be subjectId).
  'push_rule',
  // An elevation (docs/DECISIONS.md "elevation / sudo mode"): a personal,
  // time-boxed, reasoned pre-consent to overrides the person may already make.
  // Authority configuration, so it lives on the log; the event subject is the
  // `elevations` row (the person it belongs to rides the payload's personRef,
  // since person ids are not uuids - same reason push_rule uses its own row).
  'elevation',
  // A ceremony (docs/DECISIONS.md "Primitive 3: Ceremony"): a convened bundle of
  // decisions + deciders. Lifecycle events ride the `ceremony` subject (the row);
  // the executed DECISION each agenda item produces lands on its subject item's
  // timeline via the ordinary spine writer, not here.
  'ceremony',
  'machine', // RESERVED with the machine_key_* words above; unwritten until the log has a global plane
] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

/**
 * Context-resolution definitions (docs/CONTEXT_RESOLUTION.md, widened by
 * docs/FLOW.md): a `field` (an `AuthoredField` payload) or a `flow` (a
 * `FlowPayload` - ordered stages plus flow-scoped fields), each a first-class
 * row owned by a context anchor (a group or an item root), not stored on a view.
 * The `field` kind is HISTORICAL: custom fields moved onto flows 2026-07-16
 * (docs/VIEWS.md §2a, the context-field retirement). The enum value stays (the
 * `definitions.kind` pgEnum needs it; removing an enum value needs a USING cast);
 * new authoring only ever creates `flow` rows.
 */
export const DEFINITION_KINDS = ['field', 'flow'] as const;
export type DefinitionKind = (typeof DEFINITION_KINDS)[number];

/**
 * A definition's audience dial. `public` is item truth; `private` was the
 * context's annotation about the item. HISTORICAL for `private` after the
 * context-field retirement (docs/VIEWS.md §2a, 2026-07-16); flows are public.
 */
export const DEFINITION_VISIBILITIES = ['public', 'private'] as const;
export type DefinitionVisibility = (typeof DEFINITION_VISIBILITIES)[number];

/** Definition lifecycle: `archived` retires a definition without deleting it
 *  (definition durability - an adopter's protection is archive-not-delete). */
export const DEFINITION_STATUSES = ['active', 'archived'] as const;
export type DefinitionStatus = (typeof DEFINITION_STATUSES)[number];

/**
 * The alignment-ladder link enums. RETIRED 2026-07-16 with the context-field
 * system (docs/VIEWS.md §2a): the `definition_links` table was dropped. These
 * constants back the still-declared `definition_link_*` pgEnums (historical, so
 * the retirement migration drops only the table); nothing else uses them.
 */
export const DEFINITION_LINK_KINDS = ['adopts', 'same_as'] as const;
export type DefinitionLinkKind = (typeof DEFINITION_LINK_KINDS)[number];

export const DEFINITION_LINK_STATUSES = ['active', 'pending', 'severed'] as const;
export type DefinitionLinkStatus = (typeof DEFINITION_LINK_STATUSES)[number];

/**
 * Typed module keys (DESIGN.md Prinzip 10): a module is a named, **activatable per
 * group** group of typed fields layered on a work item. Five fields are mandatory on
 * every item; everything else arrives as a module a group opts into. The field
 * definitions (kinds, validation, labels) live in `domain/modules.ts`; this is just
 * the enum, the single source of truth like every other one here. Adding a module is
 * adding a key here + an entry in the registry, never a new table.
 */
export const MODULE_KEYS = ['resources', 'schedule', 'checklist'] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

/**
 * The registry modules an item ADDS as a section of its page (docs/MODULES.md,
 * 2026-09-15): pinned through "+ Add a section", removable from their own head.
 * The `checklist` module is not one of them (docs/CHECKLIST.md §2, ruled
 * 2026-11-03): every item carries its list under the head from birth, and its
 * namespace exists only while it holds lines or an order. The hosts that list
 * sections (the add menu, the phone's doors, the section screens) iterate this.
 */
export const SECTION_MODULE_KEYS = [
  'resources',
  'schedule',
] as const satisfies readonly ModuleKey[];
export type SectionModuleKey = (typeof SECTION_MODULE_KEYS)[number];

/**
 * Event level: a dev-style severity order for the accountability log (Prinzip
 * 20). Most activity is `info`; an action taken out of the expected role is
 * `warning`; a self-dealing or high-stakes irregularity (passing verdict on your
 * own delivery) is `critical`. `notice` is reserved for normal-but-noteworthy
 * moves. The log is read by filtering to a threshold. Under open-by-default the
 * level *flags* an action, it does not block it.
 */
export const LEVELS = ['info', 'notice', 'warning', 'critical'] as const;
export type Level = (typeof LEVELS)[number];

/** Each level's place in the order, `info` 0 to `critical` 3 (its index in
 *  `LEVELS`): what the fold, the `level` criterion, the `health` column and
 *  the listing statement compare a level by. */
export const LEVEL_RANK: Readonly<Record<Level, number>> = Object.fromEntries(
  LEVELS.map((level, i) => [level, i]),
) as Record<Level, number>;

/**
 * The two stances of a qualified fact (docs/HEALTH_SIGNALS_V2.md §3): the
 * human verdict added BESIDE a fact, never over it. `adopt` = "this concerns
 * my promise" (colours at the target grade); `contain` = "my promise is
 * unaffected; the blast stops here" (colourless, severs the relay). Model in
 * domain/qualified-facts.ts.
 */
export const QUALIFICATION_STANCES = ['adopt', 'contain'] as const;
export type QualificationStance = (typeof QUALIFICATION_STANCES)[number];

/**
 * The tag FAMILIES (docs/HEALTH_SIGNALS_V2.md §8): the family decides
 * behaviour, not the grade alone. Each platform health state carries its
 * family in `HEALTH_STATES` (src/domain/tags.ts, review R10).
 *
 * The `rest` family (`on_hold`) was RETIRED with the automation-into-flows
 * ruling (docs/archive/AUTOMATION_AUDIT.md, 2026-07-22): the machine no longer has an
 * `on_hold` state, the two-store `on_hold <=> not active` coupling is gone, and
 * the not-active state is just Idle (the ordinary grey glyph, MACHINE.md §1).
 *
 *   - `concern` - a concern signal: enters the collector; warning and up
 *     takes the dot and folds (the channel law), notice stays a quiet
 *     presence (HS-J2).
 *   - `mirrored` - facts wearing platform keys (disputed, unrequested):
 *     machine-mirrored from state, never declarable.
 */
export const TAG_FAMILIES = ['concern', 'mirrored'] as const;
export type TagFamily = (typeof TAG_FAMILIES)[number];

/**
 * Notification filter kind (docs/NOTIFICATIONS.md): the two per-subject overrides a viewer
 * places on their inbox. `mute` suppresses a subject you would otherwise be alerted on;
 * `subscribe` follows a subject beyond your stake. These are **private** attention settings,
 * never events in the shared log.
 */
export const NOTIFICATION_FILTER_KINDS = ['mute', 'subscribe'] as const;
export type NotificationFilterKind = (typeof NOTIFICATION_FILTER_KINDS)[number];

/**
 * The kinds of notification a person can act on (docs/NOTIFICATIONS.md "Data
 * model", the `notification_state` table). One instance of a kind is named by
 * its subject and the `seq` of the event that minted it.
 *   - `needs_you` - an ask the item page's needs-you strip puts to the viewer
 *     (docs/MOBILE.md §3): a delivery awaiting their close or review (minted by
 *     the event that set `delivered`), or a seat offered to them (minted by its
 *     `seat_offered` event). Subject: the work item.
 *   - `mentioned` - the viewer named in a prose field of an item (docs/PROSE.md
 *     §5.4): a reference to them new in that commit (`mentionsOf`,
 *     src/domain/references.ts), minted by the field's own event (`created` or
 *     `field_changed`). Subject: the work item.
 */
export const NOTIFICATION_KINDS = ['needs_you', 'mentioned'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * What a person did with one notification instance (the `act` of a
 * `notification_state` row). Only an act the person took is stored; a
 * notification merely shown has no row. **Private** attention state, never an
 * event in the shared log.
 *   - `dismissed` - hidden by the person (the needs-you strip's ✕): it stays
 *     away for that instance on every device of theirs, and a new instance
 *     shows again (Leon, 2026-10-20).
 *   - `read` - the person opened what the notification points at (a
 *     `mentioned`: the item that names them, docs/PROSE.md §5.4), so its
 *     pinned row leaves Activity; a new instance shows again.
 */
export const NOTIFICATION_ACTS = ['dismissed', 'read'] as const;
export type NotificationAct = (typeof NOTIFICATION_ACTS)[number];

/**
 * Actions the write spine no longer emits: the old status enum's lifecycle
 * moves, retired by the contract spine (docs/CONTRACT_SPINE.md §10); the single
 * free phase move `phase_changed`, retired by the command reshape (the lifecycle
 * acts replace it, docs/MACHINE_RESHAPE_BUILD_PLAN.md §1.4); and `stage_advanced`, retired by the
 * flow model (docs/FLOW.md; `flow_advanced` on the member replaces it - FLOW
 * S8 deleted the stage-children machinery). They stay in the ARRAY below
 * because the array is the pg enum's definition and the **log's historical
 * vocabulary**: Postgres cannot drop enum values, and old log rows keep their
 * meaning. Only the TS-side emission type (`EventAction`) excludes them; reads
 * use `LogEventAction` (the full union) because the log may still contain them.
 */
export const LEGACY_EVENT_ACTIONS = [
  'status_changed',
  'committed',
  'closed',
  'aborted',
  'stage_advanced',
  // The single free phase move (docs/MACHINE_RESHAPE_BUILD_PLAN.md §1.4). Retired
  // as an EMIT by the command reshape: the lifecycle acts
  // (tendered/tender_retracted/activity_declared + the conclusion acts) replace
  // it. It stays in `EVENT_ACTIONS` (append-only enum) and remains renderable +
  // revertible for historical rows; it is simply no longer emittable.
  'phase_changed',
  // The dissolution acts (docs/CLOSED_AXIS.md, three-atom re-cut 2026-07-22):
  // Dissolve/Revive are DELETED and the migration sequence CONVERTED every
  // logged row (`dissolved` -> `concluded` with the reason, `revived` ->
  // `reopened`), so unlike the other legacy actions NO row carries these -
  // they persist only as pg enum values (Postgres cannot drop enum values).
  // No renderer, decoder, or reversal path needs a case for them.
  'dissolved',
  'revived',
] as const;
export type LegacyEventAction = (typeof LEGACY_EVENT_ACTIONS)[number];

/**
 * Event actions. Grain of the taxonomy:
 *   - Meaningful contract decisions are named individually (created,
 *     phase_changed, accepted, disputed, resolved, reassigned, the seat trio),
 *     because the diagnostic value lives in distinguishing them.
 *   - `field_changed` is the generic carrier for routine requester/contributor
 *     field edits (priority, acceptance_criteria, due date, visibility, estimates).
 *     `previous_value`/`new_value` name exactly which fields changed.
 *   - `content_edited` is reserved for collaborative prose checkpoints
 *     (Live-Collaboration two-tier model); unused until that layer exists.
 *   - Edge and grant mutations get their own actions because accountability
 *     questions are often edge questions ("who created the cycle?").
 *
 * The array's ORDER is the pg enum's order (schema snapshot); never reorder,
 * only append (or ALTER TYPE ... ADD VALUE BEFORE in a hand-adjusted migration).
 */
export const EVENT_ACTIONS = [
  'created',
  'status_changed', // legacy (see LEGACY_EVENT_ACTIONS)
  'committed', // legacy
  'phase_changed', // legacy (see LEGACY_EVENT_ACTIONS): the old single phase move, retired as an emit by the command reshape (the lifecycle acts replace it); still rendered/reverted for historical rows
  'seat_offered', // a seat pushed to a party who is outside the pusher's push circle (lands offered)
  'seat_taken', // a seat landed held without an offer: a self-pick, or a direct seating inside the pusher's circle
  'seat_declined', // an offered seat refused (the formation-dispute made visible; the offer clears)
  'reassigned',
  'field_changed',
  'content_edited',
  'commented',
  'acknowledged', // a party logged "I have seen / I accept this" - accountability, not the private seen-cursor

  'resolved',
  'accepted',
  'disputed',
  'closed', // legacy
  'aborted', // legacy
  'dependency_added',
  'dependency_removed',
  'reparented',
  // Unified relationship edges (docs/RELATIONSHIPS.md): an edge created active, removed,
  // or run through the two-sided approval lane (proposed -> accepted | rejected). `kind`
  // (parent/blocks/relates) rides the event's value; subjectType is `relationship`.
  'relationship_added',
  'relationship_removed',
  'relationship_proposed',
  'relationship_accepted',
  'relationship_rejected',
  'shared', // role granted on a work item to a person/group (the access edge); newValue carries the role
  'unshared', // role grant revoked
  'access_requested', // a person asked for a role on an item (a `proposed` grant awaiting an owner)
  'member_added', // a person or group added to a group (the people-network edge); newValue carries the role
  'member_removed', // membership removed
  'nesting_proposed', // a group's owner proposed nesting it under another group (a `proposed` membership)
  'nesting_accepted', // the other group's owner accepted the nesting (membership goes active)
  'nesting_severed', // a group-in-group nesting removed
  'grant_issued', // OBO delegation issued (the `grants` table; distinct from the access edge)
  'grant_revoked',
  'module_enabled', // HISTORICAL (no longer emitted): a group turned a typed module on. Retired 2026-07-16 - registry modules are global (docs/VIEWS.md §2a); kept for replay of pre-retirement logs
  'module_disabled', // HISTORICAL (no longer emitted): a group turned a typed module off (see module_enabled)
  'module_added', // a user PINNED a global registry module onto an item (subject = work_item); the section shows even when empty (docs/VIEWS.md §2a - modules are progressive disclosure, adding is a visual pin)
  'module_removed', // a user unpinned a global registry module from an item (subject = work_item); newValue carries the moduleKey, previousValue the removed data
  'stage_advanced', // legacy (see LEGACY_EVENT_ACTIONS): the stage-children advance, retired by FLOW

  'tag_defined', // HISTORICAL (no longer emitted): a key added to an org's health-tag catalog (key + label + severity). The health states are a platform constant since 2026-10-14 (HEALTH_STATES); reserved, kept for replay of older logs
  'tag_applied', // a person manually applied a catalog tag to a work item
  'tag_cleared', // a manually applied tag removed from a work item
  'view_defined', // a saved view created (map or board; filters/config bundle; personal or group-shared)
  'view_updated', // a saved view renamed, re-scoped, or its config changed (incl. a board's hide set)
  'view_deleted', // a saved view removed
  'view_retired', // a board or a view ARCHIVED (docs/LENSES.md §5, docs/VIEWS.md §2b "Archive and Restore"): still readable, a tombstone for a view's subscribers
  'view_restored', // an archived board or view brought back (the inverse of view_retired)
  'view_access_granted', // a principal added to a view's AUDIENCE (docs/VIEWS.md §2b) — its live access
  'view_access_revoked', // a principal removed from a view's audience
  // Proposals (docs/PROPOSALS.md): a proposal is a staged changeset; subjectType is
  // `proposal`. Authoring/publishing mutate the proposal resource; **applying** a change
  // goes through the item write spine (a normal field_changed/contract event carries the
  // provenance), so the truth still changes the one sanctioned way.
  'proposal_created', // a draft proposal opened (subject = proposal); newValue carries name/owner
  'proposal_published', // a draft frozen and rolled out (the diff is now raised to owners)
  'proposal_withdrawn', // a draft retired before publish
  'change_proposed', // a field change staged into a proposal (or removed); newValue carries the target/field/value
  'change_applied', // a staged change was applied to its target item (links to the item event)
  'change_declined', // a staged change was refused by the target's owner
  'change_superseded', // a staged change was overtaken (live moved, or an addendum replaced it)
  // Plans (docs/PROPOSALS.md): an owned collection of proposals; subjectType is `plan`.
  'plan_created', // a draft plan opened (subject = plan); newValue carries name/owner
  'proposal_added_to_plan', // a draft proposal was placed into a draft plan (still its author's)
  'plan_published', // the plan owner pushed: every member draft proposal freezes, authors preserved
  // Context-resolution definitions (docs/CONTEXT_RESOLUTION.md): a field/flow
  // definition authored on a context anchor; subjectType is `definition`. The
  // payload diff rides `previousValue`/`newValue` (the audit record IS the delta).
  'definition_defined', // a field or flow definition authored on a context (subject = definition)
  'definition_changed', // a definition's payload/visibility edited (the governed, logged diff)
  'definition_archived', // a definition retired without deletion (durability)
  'definition_unarchived', // an archived definition brought back to active (the reverse of archive)
  'definition_access_granted', // a principal added to a flow's AUDIENCE (docs/VIEWS.md §2, first-class flows) — its live access
  'definition_access_revoked', // a principal removed from a flow's audience
  // The alignment ladder (rungs 2 and 3): a context references another's definition.
  'definition_adopted', // rung 3: a context adopts a definition by reference (owner notified)
  'definition_forked', // an adopter forks back into its own home (copy-on-write, reversible)
  'definition_linked', // rung 2: a same-as crosswalk declared from one definition to another
  'definition_unlinked', // a same-as / adoption link removed (reversible by the declarer)
  // A context's private annotation about an item (docs/CONTEXT_RESOLUTION.md): the
  // value lives on the (context, item) association and the event rides the CONTEXT
  // subject (subjectType `group`), so it surfaces in the group's activity and never
  // in the item's public timeline, by construction.
  'annotation_changed', // a private field value set on the (context, item) association
  // Push rules (docs/CONTRACT_SPINE.md §2, D3): a person's/group's "who may seat
  // me directly" configuration changed (set, re-circled, or reset to the default
  // circle). Subject = the push_rules row; the diff rides previous/new value.
  'push_rule_set',
  // Board pins (docs/CONTRACT_SPINE.md §9, D6): an item pinned to or unpinned from
  // a named board. Evented on the view subject (the board row); member-gated on the view
  // (group membership). The `newValue` carries the item id and the resulting curated
  // flag. A board with ≥1 pin is "curated" and runs the birth recipe on create.
  'item_pinned',
  'item_unpinned',
  // Flows (docs/FLOW.md): membership + position are logged on the MEMBER work
  // item (the legible timeline); flow-side authoring keeps the definition_* actions.
  'flow_joined', // an item joined a flow (subject = the member work item; newValue carries flowId + flowPhaseKey)
  'flow_left', // an item left a flow (subject = the member work item; previousValue carries the abandoned position)
  'flow_advanced', // a member moved to another stage (subject = the member work item; newValue carries flowId + from/to + spawned mandatory work)
  'flow_waived', // a gatekeeper waived a gate condition for this member (subject = the member work item; newValue carries flowId + flowPhaseKey + ref)
  // A member's gate gained conditions (docs/FLOW.md): pre-staged work added to a
  // gate (`addWorkToGate`) or Conditional-Go follow-ups landing on the NEXT gate
  // at accept time. Subject = the member work item; newValue carries
  // flowId + flowPhaseKey + refs (the appended condition item ids).
  'flow_condition_added',
  // Elevation / sudo mode (docs/DECISIONS.md): a person starts (`elevated`) or
  // ends early (`de_elevated`) a time-boxed, reasoned pre-consent to overrides.
  // Subject = the `elevations` row; newValue carries personRef + expiresAt (+
  // optional reason). Auto-expiry needs no event (expiresAt is the seal).
  'elevated',
  'de_elevated',
  // A decision was recorded (docs/DECISIONS.md "Primitive 2: Decision"): the
  // unifying record of a judged act. Subject = the subject work item; newValue
  // carries decisionId + outcome (advance | hold | ...) + context (flowId,
  // flowPhaseKey) + optional reason/level. This slice emits it for `hold`; the
  // existing advance/accept/dispute acts migrate onto it later.
  'decision_recorded',
  // Ceremony lifecycle (docs/DECISIONS.md "Primitive 3"): convened, an agenda
  // item prepared, an item executed (the real act ran and recorded its
  // decision) or declined, and the ceremony closed. Subject = the `ceremony`
  // row; newValue carries the ceremony/agenda-item context.
  'ceremony_convened',
  'ceremony_item_added',
  'ceremony_item_executed',
  'ceremony_item_declined',
  'ceremony_closed',
  // A participant's meeting role was set / changed / removed on a ceremony
  // (docs/DECISIONS.md participant roles). Subject = the `ceremony` row;
  // newValue carries { ref, role | null }.
  'ceremony_roster_changed',
  // Work-item invites (docs/archive/COMMONS_BUILD_PLAN.md S4): the email-bound pending
  // grant. Subject = the work item; the payload references the INVITE ID only -
  // the invited email is PII and lives solely in the `work_item_invites`
  // satellite (like `persons`), NEVER in the log.
  'invite_created', // an invite issued (or a pending one refreshed); newValue carries inviteId + role
  'invite_revoked', // a pending invite withdrawn by the inviter or an item owner
  'invite_claimed', // a pending invite converted to a real grant by its verified addressee
  'invite_expired', // a pending invite found past expiry on touch (lazy flip, never a cron)
  // The qualified fact (docs/HEALTH_SIGNALS_V2.md §3): a human's fact-scoped
  // declaration on their own item - adopt ("this concerns my promise", colours
  // at the target key's grade) or contain ("the blast stops here", colourless,
  // severs the relay). Subject = the qualifying work item; newValue carries
  // { stance, factKind, anchor, subjectId, targetKey?, tagId?, grade? }. The
  // actor is ALWAYS the human declarer (HS-F1); lapsing when the cited
  // instance clears is read-time derivation and appends NOTHING (HS-G2/F6).
  'fact_qualified',
  // A flow-automation guard tripped (docs/FLOW_DEFINITION.md §8b: "all trips
  // LOUD"): a rule's planned act was refused by no-undo / no-revisit / the hop
  // budget. Subject = the flow's DEFINITION row (the finding diagnoses the
  // authored rules, not the item); newValue carries
  // { guard, reason, ruleKey, flowId, flowVersion, originSeq }. The event IS
  // the durable finding (shape B, no projection row) - the feed and the flow
  // page surface it; the refused act itself was never appended.
  'flow_finding',
  // A FACT CROSSED ITS BOUNDARY (docs/THRESHOLDS.md §2, Leon 2026-07-28: "but
  // crossing the 'end' date should emit an event already. The crossing the
  // 'threshold' date should just emit the same event what the machinery can
  // then use to either suggest or if automated to 'set' write through").
  //
  // The crossing is the FACT's, not a signal's: a declared end date passing and
  // a silence passing a flow's threshold are the same kind of moment, and both
  // deserve the same colourless event. What consumes it decides what it means -
  // a `propose` rule turns it into an ask, a `declare` rule writes the word
  // through as an ordinary declaration. That is why the event carries no grade:
  // facts state, declarations grade (HS-B1).
  //
  // Subject = the work item; `level` is info (a fact never pages - the
  // write-through's own declaration carries the grade that does). newValue
  // carries { fact, anchorKey, boundary } where boundary is either
  // { kind: 'declared', field } (the author's own date) or
  // { kind: 'rule', flowId, ruleIndex, thresholdDays } (a flow's number). The
  // anchor is the fact INSTANCE, so a crossing fires once per episode rather
  // than once per pass. No projection row: what it records is derived by
  // construction and the event is its only durable trace (shape B, like
  // `flow_finding`; see .claude/rules/write-spine.md).
  'fact_crossed',
  // A DERIVED stage move (docs/FLOW_DEFINITION.md §4, S6): the human gesture
  // whose whole substance is dispatching the Then-acts that make the target's
  // predicate true - there is NO position write, so `flow_advanced` (a logged
  // position change) would lie here. Subject = the member work item; newValue
  // carries { flowId, from, to, dispatched: [action summaries] }, and each
  // dispatched act's own event wears the hop-0 provenance stamp citing this
  // event's seq. A STORED move keeps `flow_advanced` (the position IS logged
  // truth there).
  'flow_stage_moved',
  // The lifecycle ACTS + the activity declaration (docs/CLOSED_AXIS.md, three
  // atoms; MACHINE.md §1/§4). `phase_changed` (the old single phase-move verb)
  // split into epistemically distinct acts: `tendered`/`tender_retracted` carry
  // the DELIVERY standing (set/cleared by Tender/Retract) and
  // `activity_declared` the DECLARED activity flag (SetActive -
  // a person states in-progress; a declaration, never an act with a counterparty).
  // Each carries a Shape-A column delta over the explicit standings columns
  // (`delivered`/`active`/`closed`). `phase_changed`
  // has joined LEGACY_EVENT_ACTIONS (the command reshape's final slice deleted the
  // free SetPhase move that emitted it); historical rows still render + revert.
  'tendered', // delivery standing set (Tender): delivered=true. Orthogonal since the closed axis (docs/CLOSED_AXIS.md): delivering never closes and never touches `active`
  'tender_retracted', // delivery standing cleared (Retract): delivered=false (voids a standing verdict)
  'dissolved', // legacy (see LEGACY_EVENT_ACTIONS): deleted act, all rows converted to `concluded` by the three-atoms migration
  'revived', // legacy (see LEGACY_EVENT_ACTIONS): deleted act, all rows converted to `reopened` by the three-atoms migration
  'activity_declared', // the declared activity flag moved (SetActive): active=<bool>
  // The conclusion axis (docs/CLOSED_AXIS.md, settled 2026-07-21): `closed` is a
  // standalone settable atom, decoupled from `delivered`. `Close` is the plain
  // conclusion (closed=true, leaves `active` as declared - orthogonality);
  // `Reopen` reverses it (closed=false, and un-retires a cancelled item). The
  // legacy `closed` action above belonged to the pre-spine status enum and is
  // NOT reused - these are the conclusion axis's own verbs.
  'concluded', // the conclusion set (Close): closed=true; the optional reason rides `comment`; a cancel (closing undelivered) carries `delivered: false` in the delta and warning-unless-solo
  'reopened', // the conclusion cleared (Reopen): closed=false; delivered/verdict/active stay as they were
  // Machine keys (2026-09-06): RESERVED, currently unwritten. Minting a key is an
  // act of the global identity plane (like signup) and every event belongs to an
  // org, so the act stays off-log with its credential (src/server/machine-keys.ts)
  // until the log has a global plane (the board item "Invite-only signup: the
  // follow-ups still open"). Enum values are add-only.
  'machine_key_minted',
  'machine_key_revoked',
  // A parent's list was put in a new order (Leon, 2026-09-12: each parent
  // stores its children's order). Subject = the PARENT work item - the order is
  // its substance; since 2026-11-03 the projection is the rank map of its
  // `checklist` module, over its lines and its children (docs/CHECKLIST.md §2),
  // where it was `relationships.rank` on each child's edge before (shape B).
  // newValue { childId, afterChildId, order: { entryId: rank | null } } is the
  // move and the patch that landed (an entry is a child id or a line id),
  // previousValue the ranks it replaced.
  'children_reordered',
  // RETIRED 2026-09-22, both of them. They were the deferred close
  // (docs/RESOLUTION.md §6, built 2026-09-21): an owner's tick over open
  // sub-tasks delivered now and deferred the close, `close_deferred` taking the
  // deferral and `close_deferral_retracted` giving it back, projected into a
  // `deferred_closes` row. Leon retired the mechanism the next day: a closed
  // parent with an open child became a legal, named state when §6's conclusion
  // guard grew its "Leave them open" answer, so an owner's tick simply closes
  // and the children carry on. Nothing appends these any more and the table is
  // dropped (migration 0079).
  //
  // The WORDS stay, and must: the log is append-only, a prod store already
  // holds events carrying them, and the migration rule is add-only for enum
  // values (.claude/rules/drizzle-migrations.md). Anything reading the log back
  // ignores them explicitly - `RETIRED_ACTIONS`,
  // `src/domain/resolution/context.ts` - so a replay meets a named skip rather
  // than a default. Never reuse either word for a new mechanism.
  'close_deferred',
  'close_deferral_retracted',
  // The user tag system (docs/FIELDS_AND_TAGS.md §6, settled 2026-09-29). A
  // tag is the string on the item, nested by `/`; there is no tag entity, so
  // both acts are ITEM acts. Subject = the work item; newValue { tag, fold }
  // (the spelling as typed, and its case-folded path); the projection is one
  // `item_tags` row per (item, fold) - inserted by `item_tagged`, deleted by
  // `item_untagged` (shape B). Not the health catalog's `tag_applied`.
  'item_tagged',
  'item_untagged',
  // The stranger landing (docs/REACHABILITY.md §2.4, built 2026-10-03). An
  // owner adding a person who is a stranger to them or to the group's org
  // writes a `proposed` membership: an ask in that person's lane, which only
  // the person answers. Subject = the group. `member_proposed` newValue
  // { memberRef, role, status: 'proposed' }; `member_accepted` flips the row
  // active (previousValue { memberRef, status: 'proposed' }, newValue
  // { memberRef, role, status: 'active' }); `member_declined` deletes it
  // (previousValue { memberRef, role, status: 'proposed' }, newValue
  // { memberRef }). An owner withdrawing the ask is `member_removed`.
  'member_proposed',
  'member_accepted',
  'member_declined',
  // An offered seat accepted by the offeree (or, for a group's offer, by a
  // member on the group's behalf): the offer clears and the seat lands held.
  // Same payload as `seat_taken` (shape A, the seat column delta); the word
  // alone says an offer stood before it.
  'seat_accepted',
  // An attachment added to an item (docs/ATTACHMENTS.md §6, shape B): the
  // payload names the attachment id, kind, name, content type and bytes,
  // never the bytes themselves. The row in `attachments` is the projection.
  'attachment_added',
  // An attachment removed from its item. The row stays (removed_at set) as
  // the log's referent and keeps its bytes through the grace period, until
  // the ticker's collect pass deletes the object.
  'attachment_removed',
  // The bytes of a removed attachment deleted after the grace period, by the
  // ticker's collect pass as `automation:platform` (docs/ATTACHMENTS.md §6):
  // the row stays, `collected_at` is its projection, so the record says when
  // the bytes went. Retention (A4) will append the same word.
  'attachment_collected',
] as const;
/** The full log vocabulary: every action a READ may encounter, including the
 *  legacy ones old rows carry. Reads and renderers type against this. */
export type LogEventAction = (typeof EVENT_ACTIONS)[number];
/** The emission vocabulary: what the write spine may append TODAY. The legacy
 *  actions are excluded, so a new emitter cannot reach for a dead verb. */
export type EventAction = Exclude<LogEventAction, LegacyEventAction>;

/** The acts that name a ref in a seat: a seat landed held directly
 *  (`seat_taken`), a seat offered (`seat_offered`), a seat set
 *  (`reassigned`). Removing someone names nobody. The person shortlist's
 *  picks (`src/domain/counterparts.ts`) and the partial index they are read
 *  through (`idx_events_seat_picks`, src/db/schema.ts). */
export const SEAT_PICK_ACTIONS = [
  'seat_taken',
  'seat_offered',
  'reassigned',
] as const satisfies readonly EventAction[];

/**
 * The lifecycle STANDING-move actions (docs/CLOSED_AXIS.md): the five acts
 * that together replace the single legacy `phase_changed` verb. A reader that
 * once keyed on "a phase move happened" (drift's episode identity,
 * `read/drift.ts`) watches THIS set now. `phase_changed` itself is
 * deliberately excluded (it retired as an emit; historical rows still render
 * and revert), so a live episode boundary is drawn only by the current acts.
 * The narrower "was cancelled" classification (the feed/border crossing lanes)
 * keys on concluded-without-delivery (`concluded` whose delta carries
 * `delivered: false`), not this whole set.
 */
export const STANDING_MOVE_ACTIONS = [
  'tendered',
  'tender_retracted',
  'activity_declared',
  // The conclusion acts (docs/CLOSED_AXIS.md): a Close/Reopen is a lifecycle
  // standing move like the rest - it draws an episode boundary for drift.
  'concluded',
  'reopened',
] as const satisfies EventAction[];

/**
 * The item **note** length dials (the short-prose notice, 2026-07-20). The hard
 * cap is enforced in the pure decider (a typed error past it); the soft limit is
 * where the UI's live counter appears and gently signals over-length, while still
 * allowing submission up to the hard cap. One source of truth for both surfaces.
 */
export const NOTE_MAX_LENGTH = 500;
export const NOTE_SOFT_LIMIT = 240;
